// CVPicker — Pending (pick roles to tailor) + Tailored (download finished CVs).
//
// Pending tab: evaluated matches that haven't been tailored yet. Pick 1+ and
//              hit "Tailor N CVs →" to launch the tailor pipeline.
// Tailored tab: every tailored CV PDF under reports/, with an inline preview
//               link and a direct download.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Download, FileText, BarChart3, Trash2, Briefcase, X, Check } from 'lucide-react';
import type { ReactNode } from 'react';
import PageHeader from '../components/PageHeader';
import Chip, { fitVariant } from '../components/ui/Chip';
import DeadlineChip from '../components/ui/DeadlineChip';
import LogoSquare from '../components/ui/LogoSquare';
import { apiGet, apiPost } from '../lib/api';
import { useZh } from '../lib/useProduct';
import { friendlyError } from '../lib/errorMessages';
import { useDeleteMatch } from '../lib/useDeleteMatch';
import type { Match } from '../lib/types';

interface MatchesResponse { total: number; items: Match[] }

interface TailoredItem {
  pdf_file: string;
  pdf_url: string;
  candidate_slug: string | null;
  company_slug: string | null;
  company: string;
  date: string;
  bytes: number;
  mtime: number;
}
interface TailoredResponse { total: number; items: TailoredItem[] }

type Tab = 'pending' | 'tailored';

// Rough company-name slugifier that matches the backend convention
// (lowercase, collapse non-alphanumerics to single dash).
function companySlug(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// Single-line date for Tailored rows: relative ("2h ago") when recent, ISO date
// ("2026-04-22") otherwise. No `date · relative` duplication.
function fmtTailoredDate(mtime: number, fallbackDate: string, zh: boolean): string {
  const diffH = (Date.now() - mtime) / 3600_000;
  if (diffH < 1) {
    const mins = Math.max(1, Math.floor(diffH * 60));
    return zh ? `${mins} 分钟前` : `${mins} min ago`;
  }
  if (diffH < 48) {
    const hrs = Math.floor(diffH);
    return zh ? `${hrs} 小时前` : `${hrs} h ago`;
  }
  // Older than 2 days — show the filename's date (already in YYYY-MM-DD form)
  return fallbackDate;
}

// Compact icon-only button used in the Tailored row's secondary-action cluster.
// Accepts either `href` (renders <a>) or `onClick` (renders <button>).
interface IconButtonProps {
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  href?: string | null | undefined;
  newTab?: boolean;
  disabled?: boolean;
  danger?: boolean;
}
function IconButton({ label, icon, onClick, href, newTab, disabled, danger }: IconButtonProps) {
  const base: React.CSSProperties = {
    padding: '5px 7px',
    fontSize: 11,
    lineHeight: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: danger ? '#b43a3a' : 'var(--ink2)',
    opacity: disabled ? 0.35 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
  if (href && !disabled) {
    return (
      <a
        className="btn ghost"
        href={href}
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noreferrer' : undefined}
        title={label}
        aria-label={label}
        style={base}
      >
        {icon}
      </a>
    );
  }
  return (
    <button
      className="btn ghost"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      style={base}
    >
      {icon}
    </button>
  );
}

export default function CVPicker() {
  const zh = useZh();
  const nav = useNavigate();
  const qc = useQueryClient();
  const deleteMatch = useDeleteMatch();
  const [tab, setTab] = useState<Tab>('pending');
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [mode, setMode] = useState<'matches' | 'custom'>('matches');
  const [customUrl, setCustomUrl] = useState('');
  const [customBusy, setCustomBusy] = useState(false);
  const [customMsg, setCustomMsg] = useState<string | null>(null);
  // When set, the selected-matches query polls for a few minutes so a
  // freshly pasted-and-evaluated URL pops into the list without a refresh.
  const [recentAddAt, setRecentAddAt] = useState<number | null>(null);
  // Score-bucket multi-select filter. Replaces the old 0-100 slider —
  // see the chip block in the JSX for label semantics. Empty set = "All"
  // (show every match). `strong`/`worthALook`/`likelySkip` are the three
  // score ranges (≥80 / 60-79 / <60); the user can combine any subset
  // (e.g. Strong + Worth a look = score ≥60).
  type ScoreBucket = 'strong' | 'worthALook' | 'likelySkip';
  const [scoreBuckets, setScoreBuckets] = useState<Set<ScoreBucket>>(new Set());
  const allBucketsSelected = scoreBuckets.size === 0;
  const toggleBucket = (b: ScoreBucket) => {
    setScoreBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(b)) next.delete(b);
      else next.add(b);
      // Selecting all three = same as none → collapse to empty.
      if (next.size === 3) return new Set();
      return next;
    });
  };
  const clearBuckets = () => setScoreBuckets(new Set());
  // Pending-tab sort key. Client-side sort because pendingPool is already
  // a filtered slice of `pool` (tailored ones removed in JS), so re-sorting
  // here avoids a round-trip when the user just wants to reorder. Same
  // option set as /matches Evaluated tab to keep the two views consistent.
  const [sortKey, setSortKey] = useState<'score' | 'salary' | 'reputation' | 'posted' | 'company'>('score');
  const [resumableCount, setResumableCount] = useState(0);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('nice2job.cv.selected');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || parsed.length === 0) return;
      let live = 0;
      for (const j of parsed as Match[]) {
        if (localStorage.getItem('nice2job.job.cv-tailor-' + j.id)) live++;
      }
      setResumableCount(live > 0 ? live : parsed.length);
    } catch { /* noop */ }
  }, []);

  // Limit raised from 100 → 500 so CVPicker mirrors Matches → Evaluated
  // (which fetches with limit=200). With the old cap, a user with 149
  // evaluated matches would see CVPicker show only 100 — making the
  // count silently inconsistent across views. 500 is a safety ceiling
  // that covers any realistic user; the server-side default already
  // exceeds it for typical traffic.
  // CVPicker only ever shows rows the user marked "Save for CV", so fetch
  // exactly that set via ?selected=true (no limit). The previous ?limit=500
  // returned the top-500-by-score, so a saved role ranked below #500 (easy
  // once a user has thousands of reports) never appeared in CV Writer even
  // though the save persisted. Keyed under ['matches', …] so the Matches
  // page's invalidateQueries(['matches']) after a save refreshes this too.
  const matchesQ = useQuery({
    queryKey: ['matches', { selected: true }],
    queryFn: () => apiGet<MatchesResponse>('/api/matches', { selected: true }),
    // Poll every 15s for 5 min after a "paste a URL" add so the evaluated +
    // auto-selected role appears here on its own (eval takes ~2–3 min).
    refetchInterval: recentAddAt && Date.now() - recentAddAt < 5 * 60_000 ? 15_000 : false,
  });

  const tailoredQ = useQuery({
    queryKey: ['cv-tailored'],
    queryFn: () => apiGet<TailoredResponse>('/api/cv/tailored'),
    staleTime: 10_000,
    refetchInterval: tab === 'tailored' ? 5000 : false,
  });

  const pool = matchesQ.data?.items ?? [];
  const tailored = tailoredQ.data?.items ?? [];

  // Set of company slugs that already have at least one tailored PDF.
  const tailoredCompanySlugs = useMemo(() => {
    const s = new Set<string>();
    for (const t of tailored) if (t.company_slug) s.add(t.company_slug);
    return s;
  }, [tailored]);

  // Index matches by company slug so each tailored row can surface
  // "View posting" + "See evaluation" links.
  const matchBySlug = useMemo(() => {
    const m = new Map<string, Match>();
    for (const match of pool) {
      const slug = companySlug(match.company);
      if (slug && !m.has(slug)) m.set(slug, match);
    }
    return m;
  }, [pool]);

  // "Pending" = user-marked matches without a matching tailored PDF yet.
  // The `selected_for_cv` filter is the Phase-7 opt-in pool: CVPicker
  // only shows matches the user explicitly clicked "Save for CV" on
  // (over on /matches). Without this gate, every evaluated role —
  // sometimes hundreds — clutters CVPicker.
  //
  // BUG FIX (2026-05-12): the previous version fell back to "show
  // everything" when zero matches were selected, so deleting the last
  // saved row caused 500 evaluated matches to suddenly appear. That
  // surprise made it look like CVPicker had gone rogue. Now an empty
  // selected set renders an empty pool — the Empty-state component
  // points the user back at /matches with clear instructions, and the
  // "Paste a URL" tab remains available as a fallback entry point.
  const pendingPool = useMemo(
    () => pool.filter((m) =>
      m.selected_for_cv === true &&
      !tailoredCompanySlugs.has(companySlug(m.company)) &&
      // Drop expired roles automatically — no point tailoring a CV for
      // a closed posting. Liveness state comes from the daily sweep
      // (see web/lib/liveness-sweep.mjs); 'live' and 'unknown' both
      // pass through, only 'expired' is filtered.
      m.liveness !== 'expired'
    ),
    [pool, tailoredCompanySlugs]
  );
  const visiblePending = useMemo(() => {
    const inBucket = (s: number) => {
      if (allBucketsSelected) return true;
      if (scoreBuckets.has('strong')     && s >= 80)              return true;
      if (scoreBuckets.has('worthALook') && s >= 60 && s < 80)    return true;
      if (scoreBuckets.has('likelySkip') && s < 60)               return true;
      return false;
    };
    const filtered = pendingPool.filter((m) => inBucket(m.score ?? 0));
    // Mirror /api/matches comparators (server.js) so the sort feels the same
    // across the two views. Null reputation/salary sorts last with a stable
    // secondary key of score desc.
    const salaryKey = (m: Match) => {
      if (m.salary_max != null) return m.salary_max;
      if (m.salary_min != null) return m.salary_min;
      return -Infinity;
    };
    const cmpScore = (a: Match, b: Match) => (b.score ?? 0) - (a.score ?? 0);
    const cmps: Record<typeof sortKey, (a: Match, b: Match) => number> = {
      score:      cmpScore,
      salary:     (a, b) => salaryKey(b) - salaryKey(a),
      reputation: (a, b) => ((b.reputation ?? -1) - (a.reputation ?? -1)),
      posted:     (a, b) => (b.posted_at || '').localeCompare(a.posted_at || ''),
      company:    (a, b) => (a.company || '').localeCompare(b.company || ''),
    };
    const primary = cmps[sortKey];
    return [...filtered].sort((a, b) => primary(a, b) || cmpScore(a, b));
  }, [pendingPool, scoreBuckets, allBucketsSelected, sortKey]);
  const selected = pendingPool.filter((m) => picked[m.id]);
  const toggle = (id: string) => setPicked((p) => ({ ...p, [id]: !p[id] }));

  // "Paste a URL" → evaluate + auto-add to CV Writer. Queues an evaluate job
  // flagged cv_select:true; when the laptop worker submits the scored report,
  // the server appends its id to cv-selections.txt, so it shows up here (the
  // selected-matches query polls for a few minutes via recentAddAt).
  const submitCustomUrl = async () => {
    const url = customUrl.trim();
    if (!/^https?:\/\/\S+/i.test(url)) {
      setCustomMsg('请输入有效的职位链接(以 http:// 或 https:// 开头)。');
      return;
    }
    setCustomBusy(true);
    setCustomMsg(null);
    try {
      const res = await apiPost<{ queued: number }>('/api/evaluate-bulk', {
        items: [{ url }],
        cv_select: true,
      });
      if (res.queued > 0) {
        setCustomUrl('');
        setRecentAddAt(Date.now());
        setCustomMsg('已加入评估队列。评分完成后（约 2–3 分钟）会自动出现在「我的匹配岗位」中，无需刷新。');
        setMode('matches');
      } else {
        setCustomMsg('未能加入队列(链接可能无效)。');
      }
    } catch (e) {
      setCustomMsg(`提交失败：${(e as Error).message}`);
    } finally {
      setCustomBusy(false);
    }
  };

  // Fire tailor_cv jobs for every match in `items` then jump to the editor.
  // Writing each job_id into localStorage under the same key the editor pane's
  // useJobRunner reads means the editor opens with jobs already streaming —
  // no second click required.
  const [startBusy, setStartBusy] = useState(false);
  const [startErr, setStartErr] = useState<string | null>(null);
  const startTailor = async (items: Match[]) => {
    const runnable = items.filter((m) => m.company && m.role);
    if (runnable.length === 0 || startBusy) return;
    setStartBusy(true);
    setStartErr(null);
    sessionStorage.setItem('nice2job.cv.selected', JSON.stringify(runnable));
    // Fire all POSTs in parallel — the backend queue caps concurrency itself.
    let firstErr: unknown = null;
    let started = 0;
    await Promise.all(runnable.map(async (match) => {
      const payload = JSON.stringify({ company: match.company, role: match.role, url: match.url });
      try {
        const res = await apiPost<{ job_id: string }>('/api/jobs', { cmd: 'tailor_cv', payload });
        try { localStorage.setItem(`nice2job.job.cv-tailor-${match.id}`, res.job_id); } catch { /* noop */ }
        started++;
      } catch (e) { firstErr = firstErr || e; }
    }));
    setStartBusy(false);
    // A rejected POST leaves no job id behind, so the editor pane it would have
    // opened has nothing to stream and simply sits blank. Staying put with the
    // reason is more use than navigating to an empty editor.
    if (started === 0 && firstErr) {
      setStartErr(friendlyError(firstErr, zh));
      return;
    }
    if (firstErr) {
      setStartErr(zh
        ? `已开始 ${started}/${runnable.length} 个；其余未能开始：${friendlyError(firstErr, zh)}`
        : `Started ${started} of ${runnable.length}. The rest could not start: ${friendlyError(firstErr, zh)}`);
    }
    nav('/cv/editor');
  };

  const proceed = () => startTailor(selected);
  const tailorAll = () => startTailor(pendingPool);
  const resumeEditor = () => nav('/cv/editor');

  // Open the editor focused on a single match — used by the Tailored list's
  // "Report" button. Replaces any prior selection so only this company's pane
  // mounts, which auto-surfaces the ATS score card + missing-keywords report.
  const openReport = (match: Match) => {
    sessionStorage.setItem('nice2job.cv.selected', JSON.stringify([match]));
    nav('/cv/editor');
  };

  // Two-click armed pattern for Clear all — consistent with per-row delete UX.
  const [clearArmed, setClearArmed] = useState(false);
  useEffect(() => {
    if (!clearArmed) return;
    const t = setTimeout(() => setClearArmed(false), 3000);
    return () => clearTimeout(t);
  }, [clearArmed]);

  const clearAllTailored = async () => {
    if (tailored.length === 0) return;
    if (!clearArmed) {
      setClearArmed(true);
      return;
    }
    setClearArmed(false);
    try {
      const r = await fetch('/api/cv/tailored?confirm=true', {
        method: 'DELETE',
        headers: { 'X-Confirm-Destructive': 'yes' },
      });
      const j = await r.json();
      if (j.ok) {
        qc.invalidateQueries({ queryKey: ['cv-tailored'] });
      } else {
        alert((zh ? '清空失败：' : 'Clear failed: ') + (j.error || (zh ? '未知错误' : 'unknown')));
      }
    } catch (e) {
      alert((zh ? '清空失败：' : 'Clear failed: ') + (e as Error).message);
    }
  };

  // Two-click armed state per-PDF. `armedDelete` holds the pdf_file that's
  // armed; clicking the row's Trash once sets it, second click fires delete,
  // auto-reverts after 3s.
  const [armedDelete, setArmedDelete] = useState<string | null>(null);
  useEffect(() => {
    if (!armedDelete) return;
    const t = setTimeout(() => setArmedDelete(null), 3000);
    return () => clearTimeout(t);
  }, [armedDelete]);

  // Separate armed state for the Pending tab (evaluated-but-not-tailored
  // matches). Holds the match.id (= report filename without .md) of the row
  // that's armed. Auto-revert after 4s. Hits DELETE /api/matches/:id which
  // already exists and removes the .md + tracker TSV.
  const [armedPendingDelete, setArmedPendingDelete] = useState<string | null>(null);
  useEffect(() => {
    if (!armedPendingDelete) return;
    const t = setTimeout(() => setArmedPendingDelete(null), 4000);
    return () => clearTimeout(t);
  }, [armedPendingDelete]);

  const deletePendingMatch = (m: Match) => {
    if (armedPendingDelete !== m.id) {
      setArmedPendingDelete(m.id);
      return;
    }
    setArmedPendingDelete(null);
    // Drop from selection so post-delete the "Tailor N CVs" counter stays right
    setPicked((cur) => {
      if (!cur[m.id]) return cur;
      const { [m.id]: _gone, ...rest } = cur;
      return rest;
    });
    // Shared optimistic delete: removes the row from every ['matches'] cache
    // (incl. this page's selected set) + cascade invalidations. No full refetch.
    deleteMatch.mutate(m.id);
  };

  // Mark a pending role as applied. Optimistically flip its status to
  // 'Applied' in the cache (the row's button turns into the green "Applied ✓"
  // state, which survives a refetch because the server persists status into
  // the reports index), then POST: sets the report status + upserts the row
  // into applications.md (the Applications tracker). Fire-and-forget with a
  // revert on failure — same pattern as updateDeadline below.
  const markApplied = (m: Match) => {
    if (m.status === 'Applied') return;
    const patchStatus = (s: Match['status']) =>
      qc.setQueryData<MatchesResponse>(['matches', { selected: true }], (old) =>
        old ? { ...old, items: old.items.map((x) => (x.id === m.id ? { ...x, status: s } : x)) } : old,
      );
    patchStatus('Applied');
    apiPost(`/api/matches/${encodeURIComponent(m.id)}/applied`, {
      company: m.company, role: m.role, score: m.score, url: m.url,
    }).catch(() => patchStatus(m.status)); // revert on failure; next refetch corrects
  };

  // Per-row application deadline. Optimistically patch the cached match
  // so the countdown chip updates instantly, then PUT it. Fire-and-forget:
  // a failed write just means the next refetch reverts — acceptable for a
  // single date field. Empty value clears the deadline (server deletes it).
  const updateDeadline = (id: string, date: string) => {
    qc.setQueryData<MatchesResponse>(['matches', { selected: true }], (old) =>
      old
        ? { ...old, items: old.items.map((m) => (m.id === id ? { ...m, deadline: date || null } : m)) }
        : old,
    );
    fetch('/api/deadlines', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, date }),
    }).catch(() => { /* optimistic — next refetch corrects */ });
  };

  const deleteTailored = async (pdfFile: string, _company: string) => {
    if (armedDelete !== pdfFile) {
      setArmedDelete(pdfFile);
      return;
    }
    setArmedDelete(null);
    try {
      const r = await fetch(`/api/cv/tailored/${encodeURIComponent(pdfFile)}`, { method: 'DELETE' });
      const j = await r.json();
      if (j.ok) {
        qc.invalidateQueries({ queryKey: ['cv-tailored'] });
      } else {
        alert((zh ? '删除失败：' : 'Delete failed: ') + (j.error || (zh ? '未知错误' : 'unknown')));
      }
    } catch (e) {
      alert((zh ? '删除失败：' : 'Delete failed: ') + (e as Error).message);
    }
  };

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow={zh ? '简历定制' : 'CV writer'}
        title={zh ? '一个岗位，一份简历。' : 'Tailored CVs — one per role.'}
        sub={zh
          ? '「待定制」里挑出想投的岗位，系统按岗位要求改写你的简历；「已定制」里可以下载生成过的每一份。'
          : "Pending: pick matches to tailor deeply. Tailored: download every CV we've generated for you."}
        actions={
          tab === 'pending' ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn"
                disabled={pendingPool.length === 0 || startBusy}
                onClick={tailorAll}
                title={zh
                  ? `一次性为全部 ${pendingPool.length} 个待定制岗位生成简历`
                  : `Fire tailor_cv for all ${pendingPool.length} pending matches at once`}
              >
                {startBusy && pendingPool.length === selected.length
                  ? (zh ? '启动中…' : 'Starting…')
                  : (zh ? `全部定制 (${pendingPool.length.toLocaleString()})` : `Tailor all (${pendingPool.length.toLocaleString()})`)}
              </button>
              <button className="btn accent" disabled={!selected.length || startBusy} onClick={proceed}>
                {/* The count is always rendered. It used to be `${n || ''}`, which
                    left the Chinese label reading "定制 份 →" with a hole in it
                    whenever nothing was selected — the button's resting state. */}
                {startBusy && selected.length > 0
                  ? (zh ? `正在开始 ${selected.length} 份…` : `Starting ${selected.length}…`)
                  : (zh ? `定制 ${selected.length} 份 →` : `Tailor ${selected.length} CV${selected.length === 1 ? '' : 's'} →`)}
              </button>
            </div>
          ) : (
            <button
              className="btn"
              onClick={clearAllTailored}
              disabled={tailored.length === 0}
              title={clearArmed
                ? (zh ? '再点一次会永久删除所有已定制的简历 PDF' : 'Click again to permanently delete every tailored CV PDF')
                : (zh
                  ? '删除所有已定制的简历 PDF。评估报告和申请记录不受影响。'
                  : 'Delete every tailored CV PDF (reports/cv-*.pdf + output/cv-*.pdf). Evaluation reports and tracker are untouched.')}
              style={{
                color: clearArmed ? '#fff' : '#b43a3a',
                background: clearArmed ? '#b43a3a' : 'transparent',
                border: clearArmed ? '1px solid #b43a3a' : undefined,
                transition: 'all 120ms',
              }}
            >
              {clearArmed ? (zh ? '再次点击确认' : 'Click again to confirm') : (zh ? `清空全部定制 (${tailored.length})` : `Clear all tailored (${tailored.length})`)}
            </button>
          )
        }
      />

      {startErr && (
        <div
          style={{
            padding: '12px 16px', marginBottom: 16, borderRadius: 10, fontSize: 13, lineHeight: 1.6,
            background: 'rgba(217,119,6,0.10)', border: '1px solid rgba(217,119,6,0.35)', color: '#7c4a03',
          }}
        >
          {startErr}
        </div>
      )}

      {resumableCount > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 16px',
            background: '#eef4ee',
            border: '1px solid #cbdcce',
            borderRadius: 10,
            marginBottom: 16,
          }}
        >
          <div style={{ flex: 1, fontSize: 13 }}>
            {zh ? (
              <><strong>正在定制：</strong>上次还有 {resumableCount} 份简历在生成中。回到编辑器可以继续看进度。</>
            ) : (
              <><strong>In-progress tailoring:</strong> {resumableCount} CV{resumableCount > 1 ? 's' : ''} from your last session.
              Return to the editor to keep watching their runs.</>
            )}
          </div>
          <button className="btn accent" onClick={resumeEditor}>
            {zh ? '回到编辑器 →' : 'Resume editor →'}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 20, borderBottom: '1px solid var(--line)' }}>
        <button
          className="btn ghost"
          style={{
            border: 'none', borderRadius: 0, padding: '10px 16px',
            borderBottom: tab === 'pending' ? '2px solid var(--pri)' : '2px solid transparent',
            color: tab === 'pending' ? 'var(--ink)' : 'var(--ink3)',
            fontWeight: tab === 'pending' ? 600 : 400,
          }}
          onClick={() => setTab('pending')}
        >
          {zh ? "待定制" : "Pending"} ({pendingPool.length.toLocaleString()})
        </button>
        <button
          className="btn ghost"
          style={{
            border: 'none', borderRadius: 0, padding: '10px 16px',
            borderBottom: tab === 'tailored' ? '2px solid var(--pri)' : '2px solid transparent',
            color: tab === 'tailored' ? 'var(--ink)' : 'var(--ink3)',
            fontWeight: tab === 'tailored' ? 600 : 400,
          }}
          onClick={() => setTab('tailored')}
        >
          {zh ? "已定制" : "Tailored"} ({tailored.length.toLocaleString()})
        </button>
      </div>

      {tab === 'pending' && (
        <>
          <div style={{ display: 'flex', gap: 4, background: 'var(--line2)', padding: 3, borderRadius: 8, marginBottom: 16, maxWidth: 360 }}>
            {(['matches', 'custom'] as const).map((k) => (
              <div
                key={k}
                onClick={() => setMode(k)}
                style={{
                  flex: 1, textAlign: 'center', padding: '8px 12px', borderRadius: 6,
                  fontSize: 12.5, fontWeight: 500, cursor: 'pointer',
                  background: mode === k ? 'var(--surface)' : 'transparent',
                  color: mode === k ? 'var(--ink)' : 'var(--ink3)',
                  boxShadow: mode === k ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
                }}
              >
                {k === 'matches'
                  ? (zh ? `我的匹配岗位 (${pendingPool.length.toLocaleString()})` : `Your matches (${pendingPool.length.toLocaleString()})`)
                  : (zh ? '粘贴职位链接' : 'Paste a URL')}
              </div>
            ))}
          </div>

          {mode === 'matches' && (() => {
            // Score-bucket chips replace the old 0-100 slider. Same
            // human-friendly labels as the /matches Evaluated tab.
            // Counts here are over `pendingPool` (matches without a
            // tailored CV yet) so each chip tells the user how many CVs
            // they'd be queueing if they clicked it.
            const counts = pendingPool.reduce(
              (acc, m) => {
                const s = m.score ?? 0;
                if (s >= 80) acc.strong += 1;
                else if (s >= 60) acc.worthALook += 1;
                else acc.likelySkip += 1;
                return acc;
              },
              { strong: 0, worthALook: 0, likelySkip: 0 },
            );
            const buckets: Array<{
              key: ScoreBucket;
              label: string;
              subtitle: string;
              count: number;
              tooltip: string;
            }> = [
              { key: 'strong',     label: zh ? '强匹配' : 'Strong fit',   subtitle: '≥ 80',  count: counts.strong,      tooltip: zh ? '4.0 分以上（满分 5）——你的简历和岗位要求明显对得上，可以放心投。' : 'Score 4.0+ / 5 — your CV clearly matches the role. Tailor with confidence.' },
              { key: 'worthALook', label: zh ? '值得一看' : 'Worth a look', subtitle: '60–79', count: counts.worthALook,  tooltip: zh ? '3.0–3.9 分（满分 5）——处在边缘，建议先看一遍岗位描述再决定。' : 'Score 3.0–3.9 / 5 — borderline. Skim the JD before tailoring.' },
              { key: 'likelySkip', label: zh ? '可能跳过' : 'Likely skip',  subtitle: '< 60',  count: counts.likelySkip,  tooltip: zh ? '低于 3.0 分（满分 5）——方向或层级不太对。除非你有特别的理由，否则不建议投。' : 'Score below 3.0 / 5 — off-domain or seniority mismatch. Tailor only if you have a specific reason.' },
            ];
            return (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <Chip
                    variant={allBucketsSelected ? 'primary' : 'default'}
                    onClick={clearBuckets}
                    title={zh ? '显示全部待定制岗位。' : 'Show every pending match.'}
                  >
                    {zh ? "全部" : "All"} ({pendingPool.length.toLocaleString()})
                  </Chip>
                  {buckets.map((b) => {
                    const isOn = scoreBuckets.has(b.key);
                    return (
                    <Chip
                      key={b.key}
                      variant={isOn ? 'primary' : 'default'}
                      onClick={() => toggleBucket(b.key)}
                      title={zh
                        ? `${b.tooltip} 点击${isOn ? '取消' : '加入'}筛选（可多选）。`
                        : `${b.tooltip} Click to ${isOn ? 'remove from' : 'add to'} the active filter (multi-select).`}
                    >
                      {isOn && '✓ '}{b.label}
                      {b.subtitle && (
                        <span style={{ opacity: 0.65, fontWeight: 400 }}> · {b.subtitle}</span>
                      )}
                      {' '}({b.count.toLocaleString()})
                    </Chip>
                    );
                  })}
                  {/* Sort dropdown — same option set as the Matches Evaluated
                      tab. Sorts client-side over the already-loaded pool so
                      changing the key is instant. Reputation/salary on legacy
                      reports without those fields sort to the bottom. */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink2)', marginLeft: 4 }}>
                    {zh ? '排序' : 'Sort'}
                    <select
                      value={sortKey}
                      onChange={(e) => setSortKey(e.target.value as typeof sortKey)}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid var(--line)',
                        borderRadius: 6,
                        fontSize: 12,
                        background: 'var(--surface)',
                        color: 'var(--ink)',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="score">{zh ? "按匹配分（默认）" : "Score (default)"}</option>
                      <option value="salary">{zh ? "薪资 高→低" : "Salary high → low"}</option>
                      <option value="reputation">{zh ? "公司声誉" : "Company reputation"}</option>
                      <option value="posted">{zh ? "最新优先" : "Newest first"}</option>
                      <option value="company">{zh ? "公司 A→Z" : "Company A → Z"}</option>
                    </select>
                  </label>
                  <span style={{ fontSize: 11, color: 'var(--ink3)', marginLeft: 'auto' }}>
                    {zh
                      ? `显示 ${visiblePending.length.toLocaleString()} / ${pendingPool.length.toLocaleString()}`
                      : `${visiblePending.length.toLocaleString()} of ${pendingPool.length.toLocaleString()} shown`}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: 'var(--ink3)',
                    marginBottom: 14,
                    lineHeight: 1.55,
                    maxWidth: 760,
                  }}
                >
                  {zh
                    ? '每一行的数字是匹配分（0–100），综合了技术能力、岗位方向、薪资、地点、成长空间、工作节奏六个维度，衡量你的简历和这个岗位有多合。用上面的分档筛出真正值得投的那些。'
                    : 'The number on each row is your fit score (0–100). It reflects how well your CV matches the role across 6 dimensions — technical skills, mission, comp, location, growth, pace. Pick a bucket above to surface only those CVs worth tailoring.'}
                </div>
              </>
            );
          })()}

          {selected.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--pri-soft)', border: '1px solid #d4e2d6', borderRadius: 10, marginBottom: 16 }}>
              <div className="num" style={{ fontSize: 26, color: 'var(--pri)', lineHeight: 1 }}>{selected.length}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>
                  {zh
                    ? (selected.length === 1 ? '1 份定制简历 · 深度改写' : `${selected.length} 份定制简历 · 批量处理`)
                    : (selected.length === 1 ? 'One tailored CV · deep pass' : `${selected.length} tailored CVs · batch pass`)}
                </div>
              </div>
              <button className="btn ghost" style={{ fontSize: 12 }} onClick={() => setPicked({})}>{zh ? "清除" : "Clear"}</button>
            </div>
          )}

          {mode === 'custom' ? (
            <div className="card" style={{ padding: 36, textAlign: 'center' }}>
              <div className="serif" style={{ fontSize: 26, marginBottom: 10 }}>{zh ? "粘贴职位链接" : "Paste a job URL"}</div>
              <div style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 20 }}>
                {zh ? 'LinkedIn、Lever、Greenhouse，或任何公司的招聘页面都可以。' : 'LinkedIn, Lever, Greenhouse, or any careers page.'}
              </div>
              <input
                type="text"
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && customUrl.trim() && !customBusy) submitCustomUrl(); }}
                placeholder="https://boards.greenhouse.io/..."
                style={{ width: '100%', maxWidth: 520, padding: '12px 14px', border: '1px solid var(--line)', borderRadius: 8, fontSize: 13, fontFamily: 'Inter, var(--cjk-sans), sans-serif' }}
              />
              <div style={{ marginTop: 16 }}>
                <button
                  className="btn accent"
                  disabled={!customUrl.trim() || customBusy}
                  onClick={submitCustomUrl}
                >
                  {customBusy ? '提交中…' : (zh ? '抓取职位 →' : 'Fetch job →')}
                </button>
              </div>
              {customMsg ? (
                <div style={{ fontSize: 12, color: 'var(--ink2)', marginTop: 14, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
                  {customMsg}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 14 }}>
                  抓取该链接的职位描述 → 按你的画像评分 → 自动加入 CV Writer。
                </div>
              )}
            </div>
          ) : (
            <div className="card">
              <div className="row-head" style={{ gridTemplateColumns: '34px 1.3fr 1fr 0.5fr 0.5fr 150px 200px' }}>
                <div></div>
                {/* `key` drives the alignment/tooltip logic and must stay stable;
                    only `label` is translated. */}
                {([
                  { key: 'Company',  label: zh ? '公司' : 'Company' },
                  { key: 'Role',     label: zh ? '职位' : 'Role' },
                  { key: 'Fit',      label: zh ? '匹配度' : 'Fit' },
                  { key: 'Score',    label: zh ? '分数' : 'Score' },
                  { key: 'Deadline', label: zh ? '截止日期' : 'Deadline' },
                  { key: 'Actions',  label: zh ? '操作' : 'Actions' },
                ] as const).map(({ key: h, label }) => {
                  // Align header cells with the data row below them:
                  //   Score → right (matches `.num` right-aligned number in row)
                  //   Actions → right (matches the flex-end button cluster)
                  const rightAlign = h === 'Score' || h === 'Actions';
                  return (
                    <div
                      key={h}
                      className="tab"
                      title={
                        h === 'Score'
                          ? (zh
                              ? '总匹配分（0–100）。岗位方向、技术匹配、薪资、地点、节奏、成长六个维度的加权平均。'
                              : 'Overall fit (0-100). Weighted avg of 6 dimensions: Mission, Technical match, Comp, Location, Pace, Growth.')
                          : h === 'Fit'
                            ? (zh
                                ? '核心 / 相关 / 邻近——根据你的画像粗略判断的岗位类型匹配。'
                                : 'Primary / Secondary / Adjacent — rough archetype match derived from your profile.')
                            : h === 'Deadline'
                              ? (zh
                                  ? '为每个岗位设置投递截止日期，方便看还剩几天。'
                                  : 'Set an application deadline per role to track how many days you have left to apply.')
                              : undefined
                      }
                      style={{
                        textAlign: rightAlign ? 'right' : undefined,
                        cursor: (h === 'Score' || h === 'Fit' || h === 'Deadline') ? 'help' : undefined,
                        textDecoration: (h === 'Score' || h === 'Fit' || h === 'Deadline') ? 'underline dotted var(--ink3)' : undefined,
                      }}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
              {matchesQ.isLoading && <div className="empty">{zh ? '加载中…' : 'Loading matches…'}</div>}
              {visiblePending.length === 0 && !matchesQ.isLoading && (
                <div className="empty">
                  <span className="serif">
                    {pendingPool.length === 0
                      ? (zh ? '暂无待定制岗位。' : 'Nothing pending.')
                      : (zh ? '这个分档里没有岗位。' : 'No matches in this bucket.')}
                  </span>
                  {pendingPool.length === 0
                    ? (pool.some((m) => m.selected_for_cv)
                        ? (zh
                            ? '你收藏的岗位都已经生成过定制简历了。去「匹配职位」再收藏几个，或看看「已定制」。'
                            : 'Every match you saved for CV already has a tailored PDF. Save more on /matches, or check the Tailored tab.')
                        : (zh
                            ? '还没有收藏任何岗位。去「匹配职位」，在想投的岗位上点「收藏做简历」，然后回到这里。'
                            : 'No matches saved for CV tailoring yet. Open /matches and click "Save for CV" on roles you want a tailored CV for, then come back here.'))
                    : (zh ? '切换上方其他分档（或「全部」）查看更多。' : 'Switch to a different bucket above (or "All") to see more.')}
                </div>
              )}
              {visiblePending.map((m) => {
                const on = !!picked[m.id];
                return (
                  <div
                    key={m.id}
                    className="row"
                    onClick={() => toggle(m.id)}
                    style={{ gridTemplateColumns: '34px 1.3fr 1fr 0.5fr 0.5fr 150px 200px', background: on ? 'var(--pri-soft)' : 'transparent',
                      // A 1.12:1 tint cannot carry selection on its own;
                      // an inset left edge marks the row at a glance.
                      boxShadow: on ? 'inset 3px 0 0 var(--pri)' : undefined, cursor: 'pointer' }}
                  >
                    <div
                      style={{
                        width: 18, height: 18, borderRadius: 4,
                        border: `1.5px solid ${on ? 'var(--pri)' : 'var(--line)'}`,
                        background: on ? 'var(--pri)' : 'transparent',
                        color: '#fff', fontSize: 11, display: 'grid', placeItems: 'center',
                      }}
                    >
                      {on ? '✓' : ''}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <LogoSquare name={m.company} size={28} />
                      <span style={{ fontWeight: 500 }}>{m.company}</span>
                    </div>
                    <div style={{ color: 'var(--ink2)', fontSize: 12.5 }}>{m.role}</div>
                    <div><Chip variant={fitVariant(m.fit)}>{m.fit}</Chip></div>
                    <div className="num" style={{ fontSize: 18, textAlign: 'right', color: m.score >= 85 ? 'var(--pri)' : 'var(--ink)' }}>{m.score}</div>
                    {/* Deadline: per-role date input + live countdown. Clicking
                        the cell must not toggle the row's CV selection. */}
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}
                    >
                      <input
                        type="date"
                        value={m.deadline || ''}
                        onChange={(e) => updateDeadline(m.id, e.target.value)}
                        title={zh ? '设置投递截止日期，方便看还剩几天' : 'Set an application deadline to track days left to apply'}
                        style={{
                          padding: '3px 6px',
                          border: '1px solid var(--line)',
                          borderRadius: 6,
                          fontSize: 11,
                          background: 'var(--surface)',
                          color: 'var(--ink)',
                          fontFamily: 'inherit',
                          maxWidth: 138,
                        }}
                      />
                      {m.deadline && <DeadlineChip date={m.deadline} />}
                    </div>
                    <div
                      style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {m.url ? (
                        <a
                          className="btn"
                          href={m.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ padding: '4px 10px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          title={zh ? '打开职位原页面（在这里投递）' : 'Open the job posting (apply here)'}
                        >
                          <ExternalLink size={12} /> {zh ? '原职位' : 'Posting'}
                        </a>
                      ) : (
                        <button
                          className="btn"
                          disabled
                          style={{ padding: '4px 10px', fontSize: 11, opacity: 0.5 }}
                          title={zh ? '没有职位链接' : 'No job URL'}
                        >
                          {zh ? '原职位' : 'Posting'}
                        </button>
                      )}
                      <button
                        className="btn"
                        onClick={() => nav(`/job/${m.id}`)}
                        style={{ padding: '4px 10px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        title={zh ? '查看完整评估报告和匹配分详情' : 'See the full evaluation report + fit score breakdown'}
                      >
                        <FileText size={12} /> {zh ? '评估详情' : 'Match'}
                      </button>
                      {/* Mark applied: writes the role into the Applications
                          tracker (status=Applied) and persists the status so
                          the green "Applied ✓" survives a reload. Row stays. */}
                      {m.status === 'Applied' ? (
                        <span
                          className="btn"
                          style={{
                            padding: '4px 10px', fontSize: 11, display: 'inline-flex',
                            alignItems: 'center', gap: 4, color: '#1a7f37',
                            background: '#eaf6ec', border: '1px solid #b6e0bf', cursor: 'default',
                          }}
                          title={zh ? '已标记为已投递，可在「我的申请」里查看' : "Marked as applied — it's in your Applications tracker"}
                        >
                          <Check size={12} /> {zh ? '已投递' : 'Applied'}
                        </span>
                      ) : (
                        <button
                          className="btn"
                          onClick={() => markApplied(m)}
                          style={{ padding: '4px 10px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          title={zh ? '标记为已投递 → 加入「我的申请」（状态：Applied）' : 'Mark as applied → adds it to your Applications tracker (status: Applied)'}
                        >
                          <Check size={12} /> {zh ? '标记已投' : 'Mark applied'}
                        </button>
                      )}
                      {/* Armed delete: 1st click → red Confirm; 2nd click within
                          4s → DELETE /api/matches/:id (removes .md report +
                          tracker TSV). After delete, evaporates from this list
                          and from the global Matches Evaluated tab. */}
                      <button
                        className="btn ghost"
                        onClick={() => deletePendingMatch(m)}
                        title={
                          armedPendingDelete === m.id
                            ? 'Click again to permanently delete this evaluation'
                            : 'Delete this evaluation report (removes from Matches too)'
                        }
                        style={{
                          padding: armedPendingDelete === m.id ? '4px 10px' : '4px 7px',
                          fontSize: 11,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          color: armedPendingDelete === m.id ? '#fff' : '#b43a3a',
                          background: armedPendingDelete === m.id ? '#b43a3a' : 'transparent',
                          border: armedPendingDelete === m.id ? '1px solid #b43a3a' : undefined,
                        }}
                      >
                        {armedPendingDelete === m.id
                          ? <><X size={12} /> Sure?</>
                          : <Trash2 size={12} />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === 'tailored' && (
        <div className="card">
          <div
            className="row-head"
            style={{ gridTemplateColumns: '40px 1.5fr 1.6fr 130px minmax(300px, max-content)' }}
          >
            <div></div>
            {([
              { key: 'Company',  label: zh ? '公司' : 'Company' },
              { key: 'Role',     label: zh ? '职位' : 'Role' },
              { key: 'Tailored', label: zh ? '生成时间' : 'Tailored' },
              { key: 'Actions',  label: zh ? '操作' : 'Actions' },
            ] as const).map(({ key: h, label }) => (
              <div key={h} className="tab" style={{ textAlign: h === 'Actions' ? 'right' : undefined }}>{label}</div>
            ))}
          </div>
          {tailoredQ.isLoading && <div className="empty">{zh ? "加载定制简历…" : "Loading tailored CVs…"}</div>}
          {!tailoredQ.isLoading && tailored.length === 0 && (
            <div className="empty">
              <span className="serif">{zh ? "还没有定制简历。" : "No tailored CVs yet."}</span>
              {zh ? "在「待定制」里选一个职位，运行定制。" : "Pick a role in the Pending tab and run the tailor pass."}
            </div>
          )}
          {tailored.map((t) => {
            const match = t.company_slug ? matchBySlug.get(t.company_slug) : undefined;
            return (
              <div
                key={t.pdf_file}
                className="row"
                style={{ gridTemplateColumns: '40px 1.5fr 1.6fr 130px minmax(300px, max-content)', alignItems: 'center' }}
              >
                <LogoSquare name={t.company} size={28} />
                <div>
                  <div style={{ fontWeight: 500 }}>{t.company}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 2 }}>{fmtBytes(t.bytes)}</div>
                </div>
                <div style={{ color: 'var(--ink2)', fontSize: 12.5 }}>
                  {match?.role || <span style={{ color: 'var(--ink3)' }}>—</span>}
                </div>
                {/* Single-line date — show relative if recent, otherwise ISO date */}
                <div
                  style={{ fontSize: 12, color: 'var(--ink3)' }}
                  title={new Date(t.mtime).toISOString()}
                >
                  {fmtTailoredDate(t.mtime, t.date, zh)}
                </div>
                {/* Actions: primary "Download CV" + icon-only cluster for secondary actions */}
                <div
                  style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}
                >
                  <a
                    className="btn accent"
                    href={t.pdf_url}
                    download={t.pdf_file}
                    style={{
                      padding: '5px 12px',
                      fontSize: 11.5,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      fontWeight: 500,
                    }}
                    title={zh ? '下载定制版简历 PDF' : 'Download the tailored PDF'}
                  >
                    <Download size={12} /> {zh ? '下载简历' : 'Download CV'}
                  </a>
                  {/* Vertical separator between primary and secondary actions */}
                  <div style={{ width: 1, height: 18, background: 'var(--line)' }} />
                  <IconButton
                    label={zh ? '在新标签页预览 PDF' : 'Preview PDF in new tab'}
                    href={t.pdf_url}
                    newTab
                    icon={<ExternalLink size={13} />}
                  />
                  <IconButton
                    label={match?.url ? (zh ? '打开职位原页面' : 'Open job posting') : (zh ? '没有职位链接' : 'No job URL')}
                    href={match?.url}
                    newTab
                    disabled={!match?.url}
                    icon={<Briefcase size={13} />}
                  />
                  <IconButton
                    label={match ? 'See evaluation match' : 'No evaluation for this company'}
                    onClick={match ? () => nav(`/job/${match.id}`) : undefined}
                    disabled={!match}
                    icon={<FileText size={13} />}
                  />
                  <IconButton
                    label={match ? 'Open tailor report (ATS score delta + missing keywords)' : 'No evaluation → no report'}
                    onClick={match ? () => openReport(match) : undefined}
                    disabled={!match}
                    icon={<BarChart3 size={13} />}
                  />
                  {/* Armed-state delete: first click turns red + shows "Sure?", second click deletes */}
                  <button
                    className="btn ghost"
                    onClick={() => deleteTailored(t.pdf_file, t.company)}
                    title={armedDelete === t.pdf_file ? 'Click again to permanently delete' : 'Delete this tailored PDF'}
                    style={{
                      padding: armedDelete === t.pdf_file ? '5px 10px' : '5px 7px',
                      fontSize: 11,
                      lineHeight: armedDelete === t.pdf_file ? 1 : 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      color: armedDelete === t.pdf_file ? '#fff' : '#b43a3a',
                      background: armedDelete === t.pdf_file ? '#b43a3a' : 'transparent',
                      border: armedDelete === t.pdf_file ? '1px solid #b43a3a' : undefined,
                      transition: 'all 120ms',
                    }}
                  >
                    {armedDelete === t.pdf_file ? (<><X size={12} /> Sure?</>) : <Trash2 size={13} />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

